import type { BacktestTrade, BacktestResult } from "@/api/types";
import { formatDate, formatDateTime } from "@/lib/dates";
import type { TimeZoneMode } from "@/stores/timezone";

export interface BacktestClosedTrade {
  entry_date: string;
  exit_date: string;
  direction: string;
  entry_price: number;
  exit_price: number;
  qty: number;
  pnl: number;
  pnl_percent: number;
  fee: number;
  commission: number;
  comment: string;
  bars: number;
  is_open: boolean;
  balance: number;
}

export interface BacktestStats {
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  net_profit: number;
  net_profit_pct: number;
  gross_profit: number;
  gross_loss: number;
  profit_factor: number;
  win_rate: number;
  max_drawdown: number;
  max_drawdown_pct: number;
  sharpe_ratio: number;
  sortino_ratio: number;
  avg_win_trade: number;
  avg_loss_trade: number;
  max_consec_wins: number;
  max_consec_losses: number;
  avg_trade_duration: number;
}

/**
 * Build closed trades from raw trade records, mirroring Go's CollectClosedTrades
 * (and app/web 39e396c / b1cfce6). Entries ("long"/"short") form a FIFO queue —
 * pyramiding adds are separate fills — and closes ("close_long"/"close_short")
 * consume from the front, splitting on partial closes. Still-open fills become
 * Open rows at lastClose. Untagged legacy records fall back to action-based
 * classification.
 */
export function buildClosedTrades(
  trades: BacktestTrade[],
  lastClose: number,
  lastDate: string,
  feeRate = 0,
  fiatStart = 0,
  fiatEnd?: number
): BacktestClosedTrade[] {
  void feeRate; // signature kept for callers; open rows match Go (entry fee only)
  const closed: BacktestClosedTrade[] = [];
  const queue: { trade: BacktestTrade; remain: number; entryIdx: number }[] = [];

  const pushEntry = (t: BacktestTrade, i: number) => {
    queue.push({ trade: t, remain: t.crypto, entryIdx: i });
  };

  const closePortion = (
    t: BacktestTrade,
    i: number,
    dir: "long" | "short",
    pnl: number | null
  ) => {
    let remaining = t.crypto;
    while (remaining > 1e-12 && queue.length > 0) {
      const oe = queue[0]!;
      const c = Math.min(oe.remain, remaining);
      const entryPrice = oe.trade.price;
      const exitPrice = t.price;
      const entryCrypto = oe.trade.crypto || c;
      const proratedEntryFee = (oe.trade.fee * c) / entryCrypto;
      const exitFee = t.crypto !== 0 ? t.fee * (c / t.crypto) : t.fee;
      let portionPnl = pnl;
      if (portionPnl === null) {
        const dirSign = dir === "long" ? 1 : -1;
        portionPnl = c * (exitPrice - entryPrice) * dirSign - proratedEntryFee - exitFee;
      }
      let pnlPct = 0;
      const notional = c * entryPrice;
      if (notional !== 0) {
        pnlPct = (portionPnl / notional) * 100;
      }

      closed.push({
        entry_date: oe.trade.date,
        exit_date: t.date,
        direction: dir,
        entry_price: entryPrice,
        exit_price: exitPrice,
        qty: c,
        pnl: portionPnl,
        pnl_percent: pnlPct,
        fee: proratedEntryFee + exitFee,
        commission: proratedEntryFee + exitFee,
        comment: t.comment,
        bars: i - oe.entryIdx,
        is_open: false,
        balance: 0,
      });

      oe.remain -= c;
      remaining -= c;
      if (oe.remain <= 1e-12) {
        queue.shift();
      }
    }
  };

  for (let i = 0; i < trades.length; i++) {
    const t = trades[i]!;

    if (t.direction === "long" || t.direction === "short") {
      pushEntry(t, i);
      continue;
    }
    if (t.direction === "close_long" || t.direction === "close_short") {
      const dir = t.direction === "close_short" ? "short" : "long";
      // Engine emits one close record per portion with its own pnl.
      const taggedPnl = Number.isFinite(t.pnl) ? t.pnl : null;
      closePortion(t, i, dir, taggedPnl);
      continue;
    }

    // Untagged legacy: same side as open = pyramiding add; opposite = close.
    const thisDir: "long" | "short" = t.action === "SELL" ? "short" : "long";
    if (queue.length === 0) {
      pushEntry({ ...t, direction: thisDir }, i);
      continue;
    }
    const head = queue[0]!.trade;
    const openDir: "long" | "short" =
      head.direction === "short" || head.action === "SELL" ? "short" : "long";
    if (thisDir === openDir) {
      pushEntry({ ...t, direction: thisDir }, i);
    } else {
      closePortion(t, i, openDir, null);
    }
  }

  // Still-open fills → Open rows (unrealized; entry fee only), matching Go.
  for (const oe of queue) {
    const f = oe.trade;
    const dir = f.direction === "short" ? "short" : "long";
    const dirSign = dir === "long" ? 1 : -1;
    const priceDiff = (lastClose - f.price) * dirSign;
    const pnl = f.crypto * priceDiff - f.fee;
    let pnlPct = 0;
    const notional = f.crypto * f.price;
    if (notional !== 0) {
      pnlPct = (pnl / notional) * 100;
    }

    closed.push({
      entry_date: f.date,
      exit_date: lastDate,
      direction: dir,
      entry_price: f.price,
      exit_price: lastClose,
      qty: f.crypto,
      pnl,
      pnl_percent: pnlPct,
      fee: f.fee,
      commission: f.fee,
      comment: "",
      bars: trades.length - 1 - oe.entryIdx,
      is_open: true,
      balance: 0,
    });
  }

  // Running balance for the trade table / equity curve (UI-only field).
  let bal = fiatStart;
  for (const ct of closed) {
    if (ct.is_open && fiatEnd !== undefined && Number.isFinite(fiatEnd)) {
      ct.balance = fiatEnd;
    } else {
      bal += ct.pnl;
      ct.balance = bal;
    }
  }

  return closed;
}

/** Cumulative PnL over time. Backend does not send equity_curve; use closed-trade balances. */
export type EquityPoint = {
  date: string;
  /** Unix ms — used for a time-proportional X axis */
  t: number;
  pnl: number;
  /** True for closed-trade exits (show chart dots) */
  isTrade?: boolean;
};

function toMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const d = new Date(value);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
}

function point(date: string, pnl: number, isTrade?: boolean): EquityPoint {
  const t = toMs(date) ?? 0;
  return { date, t, pnl, isTrade };
}

export function equityCurveFromTrades(
  closed: BacktestClosedTrade[],
  fiatStart: number,
  opts?: { stored?: number[] | null; startDate?: string | null; endDate?: string | null }
): EquityPoint[] {
  const startBal = Number.isFinite(fiatStart) ? fiatStart : 0;
  const startDate = opts?.startDate || closed[0]?.entry_date || "";
  const endDate =
    opts?.endDate ||
    closed[closed.length - 1]?.exit_date ||
    startDate;

  const startMs = toMs(startDate);
  const endMs = toMs(endDate);

  let mid: EquityPoint[];
  const stored = opts?.stored;
  if (stored && stored.length > 0) {
    mid = stored.map((equity, i) => {
      const date = closed[i - 1]?.exit_date || (i === 0 ? startDate : String(i));
      return point(date, equity - startBal, i > 0);
    });
  } else if (closed.length === 0) {
    mid = [];
  } else {
    mid = closed.map((tr) => point(tr.exit_date, tr.balance - startBal, true));
  }

  const firstPnl = 0;
  const lastPnl = mid.length ? mid[mid.length - 1]!.pnl : 0;
  const out: EquityPoint[] = [];

  // Anchor at backtest start so the axis covers the full range.
  if (startDate && (mid.length === 0 || (startMs != null && mid[0]!.t > startMs))) {
    out.push(point(startDate, firstPnl, false));
  }

  out.push(...mid);

  // Anchor at backtest end so quiet periods after the last trade are visible.
  if (endDate && endMs != null) {
    const last = out[out.length - 1];
    if (!last || last.t < endMs) {
      out.push(point(endDate, lastPnl, false));
    }
  }

  return out.length ? out : [point(startDate || endDate || "", 0, false)];
}

export function computeStats(
  closedTrades: BacktestClosedTrade[],
  fiatStart: number,
  fiatEnd: number,
  backendResult?: BacktestResult & Record<string, number | undefined>
): BacktestStats {
  if (backendResult && backendResult.total_trades !== undefined) {
    const r = backendResult;
    const netProfit = r.net_profit ?? fiatEnd - fiatStart;
    // Don't trust profit_pct_fiat=0 when there is actual net profit (0 is valid
    // for empty runs, but ?? won't fall through on an explicit 0).
    let netProfitPct = r.profit_pct_fiat;
    if ((netProfitPct === undefined || netProfitPct === null || (netProfitPct === 0 && netProfit !== 0)) && fiatStart !== 0) {
      netProfitPct = (netProfit / fiatStart) * 100;
    }
    return {
      total_trades: r.total_trades ?? closedTrades.length,
      winning_trades: r.winning_trades ?? 0,
      losing_trades: r.losing_trades ?? 0,
      net_profit: netProfit,
      net_profit_pct: netProfitPct ?? 0,
      gross_profit: 0,
      gross_loss: 0,
      profit_factor: r.profit_factor ?? 0,
      win_rate: r.win_rate ?? 0,
      max_drawdown: r.max_drawdown ?? 0,
      max_drawdown_pct: r.max_drawdown_pct ?? 0,
      sharpe_ratio: r.sharpe_ratio ?? 0,
      sortino_ratio: r.sortino_ratio ?? 0,
      avg_win_trade: r.avg_win_trade ?? 0,
      avg_loss_trade: r.avg_loss_trade ?? 0,
      max_consec_wins: r.max_consec_wins ?? 0,
      max_consec_losses: r.max_consec_losses ?? 0,
      avg_trade_duration: (r.avg_trade_duration_s ?? 0) * 1000,
    };
  }

  const stats: BacktestStats = {
    total_trades: closedTrades.length,
    winning_trades: 0,
    losing_trades: 0,
    net_profit: fiatEnd - fiatStart,
    net_profit_pct: fiatStart !== 0 ? ((fiatEnd - fiatStart) / fiatStart) * 100 : 0,
    gross_profit: 0,
    gross_loss: 0,
    profit_factor: 0,
    win_rate: 0,
    max_drawdown: 0,
    max_drawdown_pct: 0,
    sharpe_ratio: 0,
    sortino_ratio: 0,
    avg_win_trade: 0,
    avg_loss_trade: 0,
    max_consec_wins: 0,
    max_consec_losses: 0,
    avg_trade_duration: 0,
  };

  if (closedTrades.length === 0) return stats;

  let grossProfit = 0;
  let grossLoss = 0;
  let consecWins = 0;
  let consecLosses = 0;

  for (const ct of closedTrades) {
    if (ct.pnl >= 0) {
      grossProfit += ct.pnl;
      stats.winning_trades++;
      consecWins++;
      consecLosses = 0;
      if (consecWins > stats.max_consec_wins) stats.max_consec_wins = consecWins;
    } else {
      grossLoss += ct.pnl;
      stats.losing_trades++;
      consecLosses++;
      consecWins = 0;
      if (consecLosses > stats.max_consec_losses) stats.max_consec_losses = consecLosses;
    }
  }

  stats.gross_profit = grossProfit;
  stats.gross_loss = grossLoss;
  const total = stats.winning_trades + stats.losing_trades;
  if (total > 0) stats.win_rate = stats.winning_trades / total;
  if (grossLoss !== 0) stats.profit_factor = grossProfit / Math.abs(grossLoss);
  if (stats.winning_trades > 0) stats.avg_win_trade = grossProfit / stats.winning_trades;
  if (stats.losing_trades > 0) stats.avg_loss_trade = grossLoss / stats.losing_trades;

  let totalDurationMs = 0;
  for (const ct of closedTrades) {
    totalDurationMs += new Date(ct.exit_date).getTime() - new Date(ct.entry_date).getTime();
  }
  stats.avg_trade_duration = totalDurationMs / closedTrades.length;

  // Max drawdown from closed-trade equity path
  let equity = fiatStart;
  let peak = fiatStart;
  let maxDd = 0;
  let maxDdPct = 0;
  for (const ct of closedTrades) {
    equity += ct.pnl;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > maxDd) {
      maxDd = dd;
      maxDdPct = peak !== 0 ? (dd / peak) * 100 : 0;
    }
  }
  stats.max_drawdown = maxDd;
  stats.max_drawdown_pct = maxDdPct;

  return stats;
}

export function formatMoney(n: number, digits = 2): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}`;
}

export function formatQty(n: number, maxDigits = 8): string {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(maxDigits).replace(/\.?0+$/, "") || "0";
}

export function formatBalance(n: number, digits = 2): string {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

export function formatPct(n: number, digits = 2): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}%`;
}

/**
 * Start-date warning for backtest/live results. Server reports fetch start
 * (`requested_first_date`, warmup included) and the first candle returned
 * (`actual_first_date`, empty = no data). Returns the message when data starts
 * later than requested; null when the range is covered.
 *
 * Tolerates one timeframe of slack for unaligned bars / zero-volume skips.
 */
export function startDateWarning(
  result: Pick<BacktestResult, "requested_first_date" | "actual_first_date" | "timeframe">,
  mode: TimeZoneMode = "utc"
): string | null {
  if (!result.requested_first_date) return null;
  const requested = new Date(result.requested_first_date).getTime();
  if (Number.isNaN(requested)) return null;
  const actualStr = result.actual_first_date ?? "";
  if (!actualStr) {
    return "No data available for this symbol/timeframe in the requested range.";
  }
  const actual = new Date(actualStr).getTime();
  if (Number.isNaN(actual)) return null;
  const tfMs = parseTimeframeMs(result.timeframe);
  if (actual - requested > Math.max(tfMs, 60_000)) {
    return `Actual start date is ${formatStartDate(actualStr, mode)} — the symbol has no data at the requested start date.`;
  }
  return null;
}

function parseTimeframeMs(tf: string): number {
  if (!tf) return 0;
  const units: Record<string, number> = { m: 60_000, h: 3_600_000, d: 86_400_000, w: 604_800_000 };
  const simple = tf.match(/^(\d+(?:\.\d+)?)([mhdw])$/);
  if (simple) return parseFloat(simple[1]!) * (units[simple[2]!] ?? 0);
  // Go duration strings like "1h30m0s"
  const re = /(\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)/g;
  const scale: Record<string, number> = {
    ns: 1e-6,
    us: 0.001,
    "µs": 0.001,
    ms: 1,
    s: 1000,
    m: 60_000,
    h: 3_600_000,
  };
  let total = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(tf)) !== null) {
    total += parseFloat(match[1]!) * (scale[match[2]!] ?? 0);
  }
  return total;
}

function formatStartDate(rfc3339: string, mode: TimeZoneMode): string {
  const full = formatDateTime(rfc3339, mode);
  if (!full) return rfc3339;
  if (full.endsWith(" 00:00")) return formatDate(rfc3339, mode) || full;
  return full;
}

